import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { getDrinkRequests } from '../../../../../services/Admin/NamedDrinksRequestApi';
import ChangeAiDrinkStatueDialog from './ChangeAiDrinkStatueDialog';
import DeleteAiDrinkRequestDialog from './DeleteAiDrinkRequestDialog';
import AiDrinkRequestDetailsDialog from './AiDrinkRequestDetailsDialog';
import { useAuth } from "../../../../../Context/AuthContext";

const AllAiDrinkRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [openEditStatus, setOpenEditStatus] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const { hasPermission } = useAuth();

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getDrinkRequests();
      setRequests(response?.data || []);
    } catch (err) {
      console.error('خطأ في جلب الطلبات:', err);
      setError(err.message || 'حدث خطأ في جلب الطلبات');
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status) => {
    const statusMap = {
      pending: { label: 'قيد الانتظار', color: 'warning' },
      approved: { label: 'موافق عليه', color: 'success' },
      rejected: { label: 'مرفوض', color: 'error' },
    };
    const statusInfo = statusMap[status] || { label: status, color: 'default' };
    return <Chip label={statusInfo.label} color={statusInfo.color} size="small" />;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const handleOpenDetails = (request) => {
    setSelectedRequest(request);
    setOpenDetails(true);
  };

  const handleCloseDetails = () => {
    setOpenDetails(false);
    setSelectedRequest(null);
  };

  const handleOpenEditStatus = (request) => {
    setSelectedRequest(request);
    setOpenEditStatus(true);
  };

  const handleCloseEditStatus = () => {
    setOpenEditStatus(false);
  };

  const handleUpdateSuccess = () => {
    // إعادة تحميل البيانات بعد التحديث الناجح
    fetchRequests();
  };

  const handleOpenDeleteDialog = (request) => {
    setSelectedRequest(request);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const uniqueStatuses = useMemo(() => {
    return [...new Set((requests || []).map((req) => req?.status).filter(Boolean))];
  }, [requests]);

  const filteredRequests = useMemo(() => {
    const q = searchText.trim().toLowerCase();

    return (requests || []).filter((request) => {
      const matchesStatus = statusFilter === 'all' || request?.status === statusFilter;
      if (!matchesStatus) return false;

      if (!q) return true;

      const haystack = [
        request?.id,
        request?.drink_name,
        request?.drink_name_en,
        request?.status,
        request?.notes,
        request?.user?.name,
        request?.user?.email,
        request?.drink?.drink_name,
      ]
        .map((val) => String(val || '').toLowerCase())
        .join(' ');

      return haystack.includes(q);
    });
  }, [requests, searchText, statusFilter]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box dir="rtl" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          جميع طلبات المشروبات المقترحة
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchRequests}
          disabled={loading}
          size="small"
        >
          تحديث
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="ابحث برقم الطلب، اسم المشروب، اسم المستخدم، البريد..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ minWidth: 280, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="status-filter-label">الحالة</InputLabel>
          <Select
            labelId="status-filter-label"
            value={statusFilter}
            label="الحالة"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">كل الحالات</MenuItem>
            {uniqueStatuses.map((status) => (
              <MenuItem key={status} value={status}>
                {status === 'pending'
                  ? 'قيد الانتظار'
                  : status === 'approved'
                  ? 'موافق عليه'
                  : status === 'rejected'
                  ? 'مرفوض'
                  : status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {filteredRequests.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <Typography variant="h6" color="text.secondary">
            لا توجد نتائج
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            لم يتم العثور على طلبات مطابقة للبحث أو الفلتر
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>رقم الطلب</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>اسم المشروب</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>اسم المشروب (إنجليزي)</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>العميل</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>البريد الإلكتروني</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>الحالة</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>التاريخ</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>الإجراء</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRequests.map((request) => (
                <TableRow
                  key={request.id}
                  sx={{
                    '&:hover': { backgroundColor: '#f9f9f9' },
                    '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                  }}
                >
                  <TableCell align="right">
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      #{request.id}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{request.drink_name}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">
                      {request.drink_name_en}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{request.user?.name || '-'}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="primary">
                      {request.user?.email || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{getStatusChip(request.status)}</TableCell>
                  <TableCell align="right">
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(request.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 1 }}>
                    {hasPermission("view") && (
                      <Button
                        startIcon={<InfoIcon />}
                        size="small"
                        variant="outlined"
                        onClick={() => handleOpenDetails(request)}
                      >
                        تفاصيل
                      </Button>
                      )}
                      {hasPermission("edit") && (
                      <Button
                        startIcon={<EditIcon />}
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => handleOpenEditStatus(request)}
                      >
                        تعديل
                      </Button>
                      )}
                      {hasPermission("delete") && (
                      <Button
                        startIcon={<DeleteIcon />}
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => handleOpenDeleteDialog(request)}
                      >
                        حذف
                      </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog لعرض التفاصيل */}
      <AiDrinkRequestDetailsDialog
        open={openDetails}
        onClose={handleCloseDetails}
        requestId={selectedRequest?.id}
      />

      {/* Dialog لتعديل الحالة */}

      <ChangeAiDrinkStatueDialog
        open={openEditStatus}
        onClose={handleCloseEditStatus}
        request={selectedRequest}
        onSuccess={handleUpdateSuccess}
      />

      {/* Dialog لحذف الطلب */}
      <DeleteAiDrinkRequestDialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        request={selectedRequest}
        onSuccess={handleUpdateSuccess}
      />
    </Box>
  );
};

export default AllAiDrinkRequests;
